import React, { useState } from 'react';

import { useGetTableEntries } from '../../../../shared/hooks/use-get-table-entries/use-get-table-entries.hook';
import { useGetTableResults } from '../../../../shared/hooks/use-get-table-results/use-get-table-results.hook';
import { useResetPageNumberAfterFiltering } from '../../../../shared/hooks/use-reset-page-number-after-filtering/use-reset-page-number-after-filtering.hook';
import { onChangeTableRowsPerPage } from '../../../../shared/utils/on-change-table-rows-per-page/on-change-table-rows-per-page.util';
import { onTablePageChange } from '../../../../shared/utils/on-table-page-change/on-table-page-change.util';
import { useAppDispatch } from '../../../../store/hooks';
import { rootActions } from '../../../../store/slices/root.slice';
import NoResults from '../../../shared/components/no-results/NoResults';
import Box from '../../../shared/components/ui/Box/Box';
import SDCTable from '../../../shared/components/ui/Table/components/table/SDCTable';
import { getPaginationConfig } from '../../../shared/components/ui/Table/utils/get-pagination-config.util';
import { bulkActionHandler } from '../../../shared/utils/bulk-action-handler/bulk-action-handler.util';
import { AppTableModel } from '../../models/app-table.model';
import { AppsFilters } from '../../models/apps-filters.model';
import { AppsHeaderAndPaginationFilters } from '../../models/apps-header-and-pagination-filters.model';
import { addPagination, resetPagination } from '../../store/slices/apps.slice';
import { getAppsTotalCount } from '../../thunks/get-apps-total-count.thunk';
import { getApps } from '../../thunks/get-apps.thunk';
import { useGetAppsTableConfig } from './hooks/use-get-apps-table-config/use-get-apps-table-config.hook';
import { useShowDeleteModalAppsTable } from './hooks/use-show-delete-modal-apps-table/use-show-delete-modal-apps-table.hook';
import { getAppsColumns } from './utils/get-apps-columns.util';

const AppsTable = (): JSX.Element => {
  const [ids, setIds] = useState<number[]>([]);
  const dispatch = useAppDispatch();

  const [imgCounter, setImgCounter] = useState<number>(0);

  const {
    appsFilters,
    tableRows,
    isLoading,
    activeMandantId,
    appsHeaderFilters,
    page,
    rowsPerPage,
    count,
  } = useGetAppsTableConfig();

  const tableIconsUrls = tableRows?.map(x => x.icon).filter(x => x);
  const nameFilter = appsFilters.header.nameOrDescription;

  useGetTableEntries<AppsFilters>(
    appsHeaderFilters,
    getAppsTotalCount,
    activeMandantId
  );
  useGetTableResults<AppsHeaderAndPaginationFilters, AppTableModel[]>(
    appsFilters,
    getApps,
    nameFilter,
    activeMandantId
  );

  const deleteModalComponent = useShowDeleteModalAppsTable(ids, appsFilters);

  useResetPageNumberAfterFiltering(count, page, rowsPerPage, resetPagination);

  const handleAppIconsLoaded = () => {
    if (imgCounter >= tableIconsUrls?.length) {
      dispatch(rootActions.setAreAppIconsLoading(false));
    } else {
      setImgCounter(p => p + 1);
    }
  };

  const handleResetAppIconsLoaded = () => {
    setImgCounter(0);
    dispatch(rootActions.setAreAppIconsLoading(true));
  };

  return (
    <>
      <Box
        sx={{
          height: '100%',
          padding: 0,
          '.table-footer': {
            minWidth: '1146px',
          },
        }}
      >
        {tableRows?.length > 0 && (
          <SDCTable
            columns={getAppsColumns(handleAppIconsLoaded)}
            rows={tableRows}
            checkboxSelection={true}
            stickyHeader={true}
            ariaLabel={'apps-table'}
            bulkActions={true}
            bulkActionHandler={bulkActionHandler(setIds)}
            sortHandler={s => console.warn(s)}
            paginationConfig={getPaginationConfig(
              count,
              rowsPerPage,
              page,
              onTablePageChange(
                dispatch,
                rowsPerPage,
                addPagination,
                handleResetAppIconsLoaded
              ),
              onChangeTableRowsPerPage(
                dispatch,
                addPagination,
                handleResetAppIconsLoaded
              )
            )}
            sx={{
              tr: {
                'th, td': {
                  '&:first-of-type': {
                    width: '36px',
                  },

                  '&:last-of-type': {
                    paddingRight: '0 !important',
                  },

                  '&:not(:first-of-type)': {
                    paddingRight: '20px',
                  },
                },
              },
            }}
          />
        )}

        {tableRows?.length === 0 && !isLoading && <NoResults />}
      </Box>

      {deleteModalComponent}
    </>
  );
};

export default AppsTable;
